import React, { useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';

import { Eye, EyeOff } from '../../../components/icons';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { SECTION_IDS } from '../../../constants/sectionIds';
import { spacing } from '../../../theme';
import { RequestFormAdditionalInformation } from '../subsections/AdditionalInformation';
import { MonitoringManagementInformation } from './subsections/MonitoringManagementInformation';
import {
  isMonitoringManagementPersistedProps,
  type MonitoringManagementProps,
  type MonitoringManagementPersistedProps,
  type MonitoringManagementInitialProps,
} from './useMonitoringManagement';

const MonitoringManagementInitialLayout: React.FC<
  MonitoringManagementInitialProps
> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  isEditing = false,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
}) => {
  return (
    <View style={monitoringManagementInitialStyles.container}>
      <PersistedStepHeader
        title="Monitoring Management"
        uomSlot={null}
        actionsSlot={null}
      />

      <MonitoringManagementInformation
        mode="initial"
        formData={formData}
        updateFormData={updateFormData}
        requestId={requestId}
        disabled={disabled}
        isEditing={Boolean(isEditing)}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
      />

      <RequestFormAdditionalInformation
        shell="formSection"
        titleSubtitle="Additional monitoring & management notes and supporting documents."
        sectionId={SECTION_IDS.MONITORING_ADDITIONAL}
        requestId={requestId}
        allowAttachments
        attachments={
          attachmentsBySection[SECTION_IDS.MONITORING_ADDITIONAL] || []
        }
        onAttachFiles={() => onAttachFiles?.(SECTION_IDS.MONITORING_ADDITIONAL)}
        onDeleteAttachment={onDeleteAttachment}
        isEditing={isEditing || !disabled}
        disabled={disabled}
        selectedFiles={selectedFiles[SECTION_IDS.MONITORING_ADDITIONAL] || []}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(SECTION_IDS.MONITORING_ADDITIONAL, files)
            : undefined
        }
        isOffline={offlineMode}
        fieldId="mgmtAdditionalRemoteMgmtInfo"
        permissionRequestStage={String(formData.stage ?? '')}
        inputLabel="Additional Monitoring & Management Information"
        placeholder="Enter additional information"
        value={String(formData.mgmtAdditionalRemoteMgmtInfo ?? '')}
        onChangeText={value =>
          updateFormData('mgmtAdditionalRemoteMgmtInfo', value)
        }
        maxLength={10000}
        inputUseEditableProp={false}
        inputDisabled={disabled}
        wrapFieldInRowColumn={false}
      />
    </View>
  );
};

const MonitoringManagementPersistedLayout: React.FC<
  MonitoringManagementPersistedProps
> = ({
  request,
  isEditing,
  editedData,
  updateEditedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits = {},
}) => {
  const reqRec = request as unknown as Record<string, unknown>;

  const getValue = useCallback(
    (field: string) => {
      const raw = isEditing
        ? editedData[field] ?? reqRec[field]
        : reqRec[field];
      if (raw === undefined || raw === null) return '';
      return String(raw);
    },
    [isEditing, editedData, reqRec],
  );

  return (
    <View style={monitoringManagementPersistedStyles.container}>
      <PersistedStepHeader
        title="Monitoring Management"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={monitoringManagementPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={monitoringManagementPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />

      <MonitoringManagementInformation
        mode="persisted"
        request={request}
        isEditing={isEditing}
        editedData={editedData}
        updateEditedData={updateEditedData}
        showFullDetails={showFullDetails}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
      />

      <RequestFormAdditionalInformation
        shell="formSection"
        titleSubtitle="Additional monitoring & management notes and supporting documents."
        sectionId={SECTION_IDS.MONITORING_ADDITIONAL}
        requestId={request.id}
        allowAttachments
        attachments={
          attachmentsBySection[SECTION_IDS.MONITORING_ADDITIONAL] || []
        }
        onAttachFiles={() => onAttachFiles?.(SECTION_IDS.MONITORING_ADDITIONAL)}
        onDeleteAttachment={onDeleteAttachment}
        isEditing={isEditing}
        disabled={!isEditing}
        readOnlyMode={!isEditing}
        selectedFiles={selectedFiles[SECTION_IDS.MONITORING_ADDITIONAL] || []}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(SECTION_IDS.MONITORING_ADDITIONAL, files)
            : undefined
        }
        isOffline={offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        fieldId="mgmtAdditionalRemoteMgmtInfo"
        permissionRequestStage={String(
          request.stage ?? request.sysRequestStage ?? '',
        )}
        showFullDetails={showFullDetails}
        inputLabel="Additional Monitoring & Management Information"
        placeholder="Enter additional information"
        value={String(getValue('mgmtAdditionalRemoteMgmtInfo'))}
        onChangeText={
          isEditing
            ? value => updateEditedData('mgmtAdditionalRemoteMgmtInfo', value)
            : () => {}
        }
        maxLength={10000}
        inputUseEditableProp
        inputEditable={isEditing}
        wrapFieldInRowColumn={false}
      />
    </View>
  );
};

const monitoringManagementInitialStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitle,
  },
});

const monitoringManagementPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
  pdfButtonContainer: {
    // PDF button will size itself
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitle,
  },
});

export const MonitoringManagement: React.FC<
  MonitoringManagementProps
> = props =>
  isMonitoringManagementPersistedProps(props) ? (
    <MonitoringManagementPersistedLayout {...props} />
  ) : (
    <MonitoringManagementInitialLayout {...props} />
  );

MonitoringManagement.displayName = 'MonitoringManagement';

/** @deprecated Test / caption suites — compose path only */
export const MonitoringManagementStep = MonitoringManagementInitialLayout;
/** @deprecated Test / caption suites — persisted request detail branch */
export const MonitoringManagementInRequest =
  MonitoringManagementPersistedLayout;
